import React from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import TqaLogo from '../assets/TQA-Logo-White.png';
import { useProject } from '../context/ProjectContext';
import { useAuth } from '../context/AuthContext';
import FolderTree from './FolderTree';
import { sharingService, userService } from '../services/api';
import SharingPicker from './SharingPicker';

const SHARED_ROOT_VALUE = 'shared-root';
const PERSONAL_ROOT_VALUE = 'personal-root';

const getPickerNodeValue = (node) => (node.isRoot
  ? (node.isPersonal ? PERSONAL_ROOT_VALUE : SHARED_ROOT_VALUE)
  : node.id);

const FolderPickerNode = React.memo(({
  node,
  depth,
  selectedValue,
  onSelect,
  allowRootSelection,
}) => {
  const [expanded, setExpanded] = React.useState(depth < 2);
  const hasChildren = (node.children || []).length > 0;
  const nodeValue = getPickerNodeValue(node);
  const isSelected = selectedValue === nodeValue;
  const canSelect = !node.isRoot || allowRootSelection;
  const paddingLeft = depth * 20 + 8;

  return (
    <>
      <div
        className="flex items-center py-1 px-2 transition-colors"
        style={{ paddingLeft, cursor: 'default' }}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={() => setExpanded((prev) => !prev)}
            className="mr-1 w-5 h-5 flex items-center justify-center flex-shrink-0"
            style={{ color: 'rgba(255,255,255,0.4)' }}
          >
            <svg
              className={`w-3.5 h-3.5 transition-transform ${expanded ? 'rotate-90' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        ) : (
          <span className="mr-1 w-5 h-5 flex-shrink-0" />
        )}

        <button
          type="button"
          disabled={!canSelect}
          onClick={() => canSelect && onSelect(nodeValue)}
          className={`flex items-center w-full text-left px-1 py-1 transition-colors ${
            canSelect ? 'cursor-pointer' : 'cursor-default'
          }`}
          style={isSelected ? { backgroundColor: 'rgba(130,255,207,0.12)', color: 'var(--tqa-green)' } : {}}
        >
          <svg className="w-4 h-4 mr-2 flex-shrink-0" style={{ color: 'var(--tqa-green)' }} fill="currentColor" viewBox="0 0 20 20">
            <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
          </svg>
          <span className={`text-sm truncate ${node.isRoot ? 'font-semibold text-white' : ''}`} style={{ color: isSelected ? 'var(--tqa-green)' : 'rgba(255,255,255,0.75)' }}>
            {node.name}
          </span>
        </button>
      </div>

      {hasChildren && expanded && (
        <div>
          {node.children.map((child) => (
            <FolderPickerNode
              key={child.id}
              node={child}
              depth={depth + 1}
              selectedValue={selectedValue}
              onSelect={onSelect}
              allowRootSelection={allowRootSelection}
            />
          ))}
        </div>
      )}
    </>
  );
});

FolderPickerNode.displayName = 'FolderPickerNode';

const Sidebar = ({
  isCollapsed = false,
  onToggleCollapse,
  width = 320,
  minWidth = 240,
  maxWidth = 480,
  isDesktop = true,
  onResize,
}) => {
  const location = useLocation();
  const { projectId } = useParams();
  const { 
    projects, 
    folders,
    currentFolder,
    isCrossProjectMode,
    isConfigurationMode,
    isDashboardMode,
    createProject,
    createFolder,
    updateFolder,
    deleteFolder,
    moveFolder,
    moveProject,
    switchToProject,
    selectFolder
  } = useProject();
  const { user, logout } = useAuth();
  const [showNewProjectModal, setShowNewProjectModal] = React.useState(false);
  const [showNewFolderModal, setShowNewFolderModal] = React.useState(false);
  const [newProjectName, setNewProjectName] = React.useState('');
  const [newFolderName, setNewFolderName] = React.useState('');
  const [selectedProjectFolderId, setSelectedProjectFolderId] = React.useState(null);
  const [selectedParentFolderId, setSelectedParentFolderId] = React.useState(null);
  const [showProjectFolderPicker, setShowProjectFolderPicker] = React.useState(false);
  const [showParentFolderPicker, setShowParentFolderPicker] = React.useState(false);
  const [error, setError] = React.useState('');
  const [creating, setCreating] = React.useState(false);
  const [projectSearchTerm, setProjectSearchTerm] = React.useState('');
  const [isSharedExpanded, setIsSharedExpanded] = React.useState(true);
  const [isPersonalExpanded, setIsPersonalExpanded] = React.useState(true);
  const [shareWithAll, setShareWithAll] = React.useState(true);
  const [shareUserIds, setShareUserIds] = React.useState(new Set());
  const [shareableUsers, setShareableUsers] = React.useState([]);
  const [shareSearchQuery, setShareSearchQuery] = React.useState('');
  const isResizingRef = React.useRef(false);

  const clampWidth = React.useCallback((value) => (
    Math.min(maxWidth, Math.max(minWidth, value))
  ), [maxWidth, minWidth]);

  const stopResizing = React.useCallback(() => {
    if (!isResizingRef.current) {
      return;
    }

    isResizingRef.current = false;
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
  }, []);

  React.useEffect(() => {
    const handlePointerMove = (event) => {
      if (!isResizingRef.current || !isDesktop || isCollapsed || typeof onResize !== 'function') {
        return;
      }

      onResize(clampWidth(event.clientX));
    };

    const handlePointerUp = () => {
      stopResizing();
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
      stopResizing();
    };
  }, [clampWidth, isCollapsed, isDesktop, onResize, stopResizing]);

  const handleResizePointerDown = React.useCallback((event) => {
    if (!isDesktop || isCollapsed || typeof onResize !== 'function') {
      return;
    }

    event.preventDefault();
    isResizingRef.current = true;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
  }, [isCollapsed, isDesktop, onResize]);

  const handleResizeKeyDown = React.useCallback((event) => {
    if (!isDesktop || isCollapsed || typeof onResize !== 'function') {
      return;
    }

    const step = 16;

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      onResize(clampWidth(width - step));
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      onResize(clampWidth(width + step));
      return;
    }

    if (event.key === 'Home') {
      event.preventDefault();
      onResize(minWidth);
      return;
    }

    if (event.key === 'End') {
      event.preventDefault();
      onResize(maxWidth);
    }
  }, [clampWidth, isCollapsed, isDesktop, maxWidth, minWidth, onResize, width]);

  const normalizedProjectSearch = React.useMemo(
    () => projectSearchTerm.trim().toLowerCase(),
    [projectSearchTerm]
  );

  const hasActiveProjectSearch = normalizedProjectSearch.length > 0;

  const folderMatchesSearch = React.useCallback((folder) => {
    if (folder.name.toLowerCase().includes(normalizedProjectSearch)) {
      return true;
    }

    const projectMatch = (folder.projects || []).some((project) => (
      project.name.toLowerCase().includes(normalizedProjectSearch)
    ));

    if (projectMatch) {
      return true;
    }

    return (folder.children || []).some(folderMatchesSearch);
  }, [normalizedProjectSearch]);

  const hasSharedMatches = React.useMemo(() => {
    if (!hasActiveProjectSearch) {
      return (folders.shared || []).length > 0;
    }
    return (folders.shared || []).some(folderMatchesSearch);
  }, [folders.shared, hasActiveProjectSearch, folderMatchesSearch]);

  const hasOrphanedSharedMatches = React.useMemo(() => {
    if (!hasActiveProjectSearch) {
      return (folders.orphanedShared || []).length > 0;
    }
    return (folders.orphanedShared || []).some((project) => (
      project.name.toLowerCase().includes(normalizedProjectSearch)
    ));
  }, [folders.orphanedShared, hasActiveProjectSearch, normalizedProjectSearch]);

  const hasPersonalMatches = React.useMemo(() => {
    if (!hasActiveProjectSearch) {
      return (folders.personal || []).length > 0;
    }
    return (folders.personal || []).some(folderMatchesSearch);
  }, [folders.personal, hasActiveProjectSearch, folderMatchesSearch]);

  const folderPickerTree = React.useMemo(
    () => [
      {
        id: SHARED_ROOT_VALUE,
        name: 'Shared',
        isRoot: true,
        isPersonal: false,
        children: (folders.shared || []).map(function mapFolder(folder) {
          return {
            id: folder.id,
            name: folder.name,
            isRoot: false,
            isPersonal: false,
            children: (folder.children || []).map(mapFolder),
          };
        }),
      },
      {
        id: PERSONAL_ROOT_VALUE,
        name: 'Personal',
        isRoot: true,
        isPersonal: true,
        children: (folders.personal || []).map(function mapFolder(folder) {
          return {
            id: folder.id,
            name: folder.name,
            isRoot: false,
            isPersonal: true,
            children: (folder.children || []).map(mapFolder),
          };
        }),
      },
    ],
    [folders]
  );

  const folderMetaMap = React.useMemo(() => {
    const map = new Map();

    const walk = (node) => {
      map.set(getPickerNodeValue(node), {
        isRoot: node.isRoot,
        isPersonal: node.isPersonal,
        name: node.name,
      });
      (node.children || []).forEach(walk);
    };

    folderPickerTree.forEach(walk);
    return map;
  }, [folderPickerTree]);

  const hasAnySelectableFolder = React.useMemo(
    () => folderPickerTree.some((root) => (root.children || []).length > 0),
    [folderPickerTree]
  );

  const initials = user?.name
    ? user.name.split(' ').map((part) => part[0]).join('').slice(0, 2).toUpperCase()
    : user?.email?.[0]?.toUpperCase() || '?';

  // Load shareable users when needed
  const loadShareableUsers = React.useCallback(async () => {
    try {
      const res = await userService.getShareableUsers();
      setShareableUsers(res.data || res);
    } catch (err) {
      console.error('Failed to load shareable users:', err);
    }
  }, []);

  // Check if selected folder is in the shared section
  const isSelectedFolderShared = React.useCallback((folderId) => {
    if (!folderId) return false;
    const meta = folderMetaMap.get(folderId);
    return meta && !meta.isPersonal;
  }, [folderMetaMap]);

  // Reset sharing state
  const resetSharingState = React.useCallback(() => {
    setShareWithAll(true);
    setShareUserIds(new Set(user?.id ? [user.id] : []));
    setShareSearchQuery('');
  }, [user]);

  // Load parent folder sharing to use as default when creating in a restricted folder
  const loadParentSharing = React.useCallback(async (folderId) => {
    if (!folderId || folderId === SHARED_ROOT_VALUE) return;
    try {
      const res = await sharingService.getFolderSharing(folderId);
      const data = res.data || res;
      if (!data.sharedWithAll) {
        setShareWithAll(false);
        const ids = new Set(data.users.map(u => u.id));
        if (user?.id) ids.add(user.id);
        setShareUserIds(ids);
      }
    } catch (err) {
      // Ignore - will default to share with all
    }
  }, [user]);

  const handleCreateProject = async (e) => {
    e.preventDefault();
    setError('');

    if (!newProjectName.trim()) {
      setError('Project name is required');
      return;
    }

    if (!selectedProjectFolderId) {
      setError('Please select a folder');
      return;
    }

    if (isSelectedFolderShared(selectedProjectFolderId) && !shareWithAll && shareUserIds.size === 0) {
      setError('Please select at least one user, or share with everyone');
      return;
    }

    setCreating(true);
    try {
      const result = await createProject(newProjectName.trim(), selectedProjectFolderId);

      // Apply custom sharing if not sharing with all (and in shared section)
      if (isSelectedFolderShared(selectedProjectFolderId) && !shareWithAll && shareUserIds.size > 0) {
        // The project inherits folder sharing on backend, but if user customized it, override
        try {
          const projectId = result?.id || result?.data?.id;
          if (projectId) {
            await sharingService.updateProjectSharing(projectId, Array.from(shareUserIds));
          }
        } catch (err) {
          console.warn('Failed to set project sharing:', err);
        }
      }

      setShowNewProjectModal(false);
      setNewProjectName('');
      setSelectedProjectFolderId(null);
      setShowProjectFolderPicker(false);
      resetSharingState();
    } catch (err) {
      setError(err.message || 'Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  const handleCreateFolder = async (e) => {
    e.preventDefault();
    setError('');

    if (!newFolderName.trim()) {
      setError('Folder name is required');
      return;
    }

    if (!selectedParentFolderId) {
      setError('Please select a parent folder');
      return;
    }

    const selectedOption = folderMetaMap.get(selectedParentFolderId);
    const isShared = selectedOption && !selectedOption.isPersonal;

    if (isShared && !shareWithAll && shareUserIds.size === 0) {
      setError('Please select at least one user, or share with everyone');
      return;
    }

    setCreating(true);
    try {
      const parentId = selectedOption?.isRoot ? null : selectedParentFolderId;
      const isPersonal = selectedOption?.isPersonal || false;

      const result = await createFolder(newFolderName.trim(), parentId, isPersonal);

      // Apply custom sharing if not sharing with all (and in shared section)
      if (isShared && !shareWithAll && shareUserIds.size > 0) {
        try {
          const folderId = result?.id || result?.data?.id;
          if (folderId) {
            await sharingService.updateFolderSharing(folderId, Array.from(shareUserIds));
          }
        } catch (err) {
          console.warn('Failed to set folder sharing:', err);
        }
      }

      setShowNewFolderModal(false);
      setNewFolderName('');
      setSelectedParentFolderId(null);
      setShowParentFolderPicker(false);
      resetSharingState();
    } catch (err) {
      setError(err.message || 'Failed to create folder');
    } finally {
      setCreating(false);
    }
  };

  const handleCancelNewProject = () => {
    setShowNewProjectModal(false);
    setNewProjectName('');
    setSelectedProjectFolderId(null);
    setShowProjectFolderPicker(false);
    setError('');
    resetSharingState();
  };

  const handleCancelNewFolder = () => {
    setShowNewFolderModal(false);
    setNewFolderName('');
    setSelectedParentFolderId(null);
    setShowParentFolderPicker(false);
    setError('');
    resetSharingState();
  };

  const handleFolderCreate = async (name, parentId = null) => {
    try {
      await createFolder(name, parentId, false);
    } catch (err) {
      console.error('Failed to create folder:', err);
      throw err; // Let FolderTree handle the error display
    }
  };

  const handleFolderRename = async (folderId, name) => {
    try {
      await updateFolder(folderId, name);
    } catch (err) {
      console.error('Failed to rename folder:', err);
      throw err; // Let FolderTree handle the error display
    }
  };

  const handleFolderDelete = async (folderId) => {
    try {
      await deleteFolder(folderId);
    } catch (err) {
      console.error('Failed to delete folder:', err);
      throw err; // Let FolderTree handle the error display
    }
  };

  const handleProjectCreateInFolder = (folderId) => {
    setSelectedProjectFolderId(folderId);
    setShowProjectFolderPicker(false);
    setShowNewProjectModal(true);
    // Eagerly load users for the sharing picker
    if (isSelectedFolderShared(folderId)) loadShareableUsers();
  };

  const selectedProjectFolderLabel = selectedProjectFolderId
    ? folderMetaMap.get(selectedProjectFolderId)?.name
    : null;

  const selectedParentFolderLabel = selectedParentFolderId
    ? folderMetaMap.get(selectedParentFolderId)?.name
    : null;

  if (isCollapsed) {
    return (
      <>
        <aside className="sidebar p-2 flex flex-col h-screen items-center" style={{ width: `${width}px` }}>
          <Link to="/" className="mt-1 flex-shrink-0 hover:opacity-80 transition-opacity" title="Project Hub">
            <img src={TqaLogo} alt="TQA" className="h-8 w-8 object-contain" />
          </Link>
        </aside>

        <button
          type="button"
          onClick={onToggleCollapse}
          className="fixed -ml-3 top-1/2 -translate-y-1/2 w-6 h-12 flex items-center justify-center transition-colors z-20"
          style={{ left: `${width}px`, backgroundColor: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)', color: 'rgba(255,255,255,0.75)' }}
          aria-label="Expand sidebar"
          title="Expand sidebar"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </>
    );
  }

  return (
    <>
      <aside className="sidebar p-6 flex flex-col h-screen" style={{ width: `${width}px` }}>
        {/* Logo/Header */}
        <Link to="/" className="mb-6 flex items-center space-x-3 flex-shrink-0 hover:opacity-80 transition-opacity">
          <img src={TqaLogo} alt="TQA" className="h-10 w-auto" />
          <h1 className="text-sm font-bold text-white uppercase tracking-widest">Project Hub</h1>
        </Link>

      {/* Projects Header */}
      <div className="pt-4 flex-shrink-0" style={{ borderTop: '1px solid var(--tqa-border-dark)' }}>
        <h2 className="p-2 text-xs font-semibold text-white mb-2 flex items-center justify-between uppercase tracking-widest">
          <span>Projects</span>
          <div className="flex gap-1">
            <button
              onClick={() => { setShowNewFolderModal(true); loadShareableUsers(); }}
              className="text-xs px-2 py-1 font-medium uppercase tracking-wider transition-colors"
              style={{ backgroundColor: 'var(--tqa-surface-2)', color: 'rgba(255,255,255,0.6)' }}
              title="New Folder"
            >
              + Folder
            </button>
            <button
              onClick={() => { setShowNewProjectModal(true); loadShareableUsers(); }}
              className="text-xs px-2 py-1 font-medium uppercase tracking-wider transition-colors hover:brightness-110"
              style={{ backgroundColor: 'var(--tqa-green)', color: 'var(--tqa-black)' }}
              title="New Project"
            >
              + Project
            </button>
          </div>
        </h2>
        <div className="px-2 pb-2">
          <input
            type="text"
            value={projectSearchTerm}
            onChange={(e) => setProjectSearchTerm(e.target.value)}
            placeholder="Search"
            className="w-full px-2 py-1 text-xs text-white focus:outline-none"
            style={{ backgroundColor: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)' }}
            aria-label="Search projects and folders"
          />
        </div>
      </div>
      
      {/* Scrollable Projects & Folders Section */}
      <div className="flex-1 overflow-y-auto min-h-0">
          {/* Shared Folders Section */}
          {(hasSharedMatches || hasOrphanedSharedMatches) && (
            <div className="mb-4">
              <h3
                className="text-xs font-semibold uppercase tracking-widest mb-2 px-2 font-mono flex items-center justify-between cursor-pointer select-none"
                style={{ color: 'rgba(255,255,255,0.35)' }}
                onClick={() => setIsSharedExpanded(!isSharedExpanded)}
              >
                <span>Shared</span>
                <svg
                  className={`w-3.5 h-3.5 transition-transform ${isSharedExpanded ? '' : '-rotate-90'}`}
                  fill="none" stroke="currentColor" viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </h3>
              {isSharedExpanded && (
                <>
                  {hasSharedMatches && (
                    <FolderTree
                      folders={folders.shared}
                      projects={[]}
                      currentProjectId={projectId}
                      currentFolderId={currentFolder?.id}
                      onProjectSelect={switchToProject}
                      onFolderSelect={selectFolder}
                      onFolderCreate={handleFolderCreate}
                      onFolderRename={handleFolderRename}
                      onFolderDelete={handleFolderDelete}
                      onFolderMove={moveFolder}
                      onProjectMove={moveProject}
                      onProjectCreate={handleProjectCreateInFolder}
                      searchTerm={projectSearchTerm}
                      isPersonal={false}
                    />
                  )}

                  {/* Orphaned Shared Projects (not in any folder) */}
                  {hasOrphanedSharedMatches && (
                    <div className="mt-2">
                      {hasSharedMatches && (
                        <h3 className="text-xs font-semibold uppercase tracking-widest mb-2 px-2 font-mono" style={{ color: 'rgba(255,255,255,0.35)' }}>
                          Uncategorized
                        </h3>
                      )}
                      <FolderTree
                        folders={[]}
                        projects={folders.orphanedShared}
                        currentProjectId={projectId}
                        currentFolderId={currentFolder?.id}
                        onProjectSelect={switchToProject}
                        onFolderSelect={selectFolder}
                        onFolderCreate={handleFolderCreate}
                        onFolderRename={handleFolderRename}
                        onFolderDelete={handleFolderDelete}
                        onFolderMove={moveFolder}
                        onProjectMove={moveProject}
                        onProjectCreate={handleProjectCreateInFolder}
                        searchTerm={projectSearchTerm}
                        isPersonal={false}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          )}
          
          {/* Personal Workspace Section - Always visible */}
          <div className="mt-4 pt-4" style={{ borderTop: '1px solid var(--tqa-border-dark)' }}>
            <h3
              className="text-xs font-semibold uppercase tracking-widest mb-2 px-2 font-mono flex items-center justify-between cursor-pointer select-none"
              style={{ color: 'rgba(255,255,255,0.35)' }}
              onClick={() => setIsPersonalExpanded(!isPersonalExpanded)}
            >
              <span>Personal Workspace</span>
              <svg
                className={`w-3.5 h-3.5 transition-transform ${isPersonalExpanded ? '' : '-rotate-90'}`}
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </h3>
            {isPersonalExpanded && (
              hasPersonalMatches ? (
                <FolderTree
                  folders={folders.personal}
                  projects={[]}
                  currentProjectId={projectId}
                  currentFolderId={currentFolder?.id}
                  onProjectSelect={switchToProject}
                  onFolderSelect={selectFolder}
                  onFolderCreate={handleFolderCreate}
                  onFolderRename={handleFolderRename}
                  onFolderDelete={handleFolderDelete}
                  onFolderMove={moveFolder}
                  onProjectMove={moveProject}
                  onProjectCreate={handleProjectCreateInFolder}
                  searchTerm={projectSearchTerm}
                  isPersonal={true}
                />
              ) : (
                <p className="text-xs px-2 py-2" style={{ color: 'rgba(255,255,255,0.3)' }}>
                  {hasActiveProjectSearch ? 'No matches found' : 'No personal folders yet'}
                </p>
              )
            )}
          </div>

          {hasActiveProjectSearch && !hasSharedMatches && !hasOrphanedSharedMatches && !hasPersonalMatches && (
            <p className="text-xs px-2 py-2" style={{ color: 'rgba(255,255,255,0.35)' }}>
              No matches found
            </p>
          )}
      </div>

      {/* Cross-projects Analysis */}
      <div className="mt-2 flex-shrink-0" style={{ borderTop: '1px solid var(--tqa-border-dark)' }}>
        <Link to="/cross-projects">
          <h2
            className="p-2 pt-4 text-xs font-semibold mb-1 flex items-center justify-between cursor-pointer transition-colors duration-150 ease-in-out uppercase tracking-widest"
            style={isCrossProjectMode
              ? { color: 'var(--tqa-green)', backgroundColor: 'rgba(130,255,207,0.08)' }
              : { color: 'rgba(255,255,255,0.5)' }}
          >
            <span>Cross-project analysis</span>
          </h2>
        </Link>
      </div>

      {/* Admin Links - Fixed at bottom */}
      {user?.role === 'admin' && (
        <div className="pt-4 flex-shrink-0" style={{ borderTop: '1px solid var(--tqa-border-dark)' }}>
          <Link to="/dashboard">
            <h2
              className="p-2 text-xs font-semibold mb-1 flex items-center justify-between cursor-pointer uppercase tracking-widest transition-colors duration-150"
              style={isDashboardMode
                ? { color: 'var(--tqa-green)', backgroundColor: 'rgba(130,255,207,0.08)' }
                : { color: 'rgba(255,255,255,0.5)' }}
            >
              <span>Dashboard</span>
            </h2>
          </Link>
          <Link to="/configuration">
            <h2
              className="p-2 text-xs font-semibold mb-1 flex items-center justify-between cursor-pointer uppercase tracking-widest transition-colors duration-150"
              style={isConfigurationMode
                ? { color: 'var(--tqa-green)', backgroundColor: 'rgba(130,255,207,0.08)' }
                : { color: 'rgba(255,255,255,0.5)' }}
            >
              <span>Configuration</span>
            </h2>
          </Link>
        </div>
      )}

      {/* User Info - Fixed at bottom */}
      {user && (
        <div className="pt-4 flex-shrink-0" style={{ borderTop: '1px solid var(--tqa-border-dark)' }}>
          <div className="flex items-center gap-3">
            {user.avatarUrl ? (
              <img
                src={user.avatarUrl}
                alt={user.name || user.email}
                className="h-8 w-8 rounded-full flex-shrink-0"
                style={{ border: '1px solid var(--tqa-border-dark)' }}
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className="h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0" style={{ backgroundColor: 'var(--tqa-green)', color: 'var(--tqa-black)' }}>
                {initials}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user.name || user.email}</p>
              <p className="text-xs truncate" style={{ color: 'rgba(255,255,255,0.4)' }}>{user.email}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={logout}
            className="mt-3 w-full py-2 text-xs font-medium uppercase tracking-widest transition-colors duration-150 hover:brightness-110"
            style={{ border: '1px solid var(--tqa-border-dark)', color: 'rgba(255,255,255,0.5)', backgroundColor: 'transparent' }}
          >
            Sign out
          </button>
        </div>
      )}

        {/* New Project Modal */}
        {showNewProjectModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0,0,0,0.75)' }}>
          <div className="p-6 w-96" style={{ backgroundColor: 'var(--tqa-surface)', border: '1px solid var(--tqa-border-dark)' }}>
            <h3 className="text-sm font-bold text-white uppercase tracking-widest mb-4">Create New Project</h3>
            <form onSubmit={handleCreateProject}>
              <div className="mb-4">
                <label htmlFor="project-name" className="block text-xs font-medium uppercase tracking-wider mb-2" style={{ color: 'rgba(255,255,255,0.5)' }}>
                  Project Name <span style={{ color: 'var(--tqa-red)' }}>*</span>
                </label>
                <input
                  type="text"
                  id="project-name"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  className="w-full px-3 py-2 text-sm text-white focus:outline-none focus:ring-1"
                  style={{ backgroundColor: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)', focusRingColor: 'var(--tqa-green)' }}
                  placeholder="Enter project name"
                  autoFocus
                />
                {error && <p className="text-xs mt-1" style={{ color: 'var(--tqa-red)' }}>{error}</p>}
              </div>
              <div className="mb-4">
                <label htmlFor="project-folder" className="block text-xs font-medium uppercase tracking-wider mb-2" style={{ color: 'rgba(255,255,255,0.5)' }}>
                  Folder <span style={{ color: 'var(--tqa-red)' }}>*</span>
                </label>
                <button
                  type="button"
                  id="project-folder"
                  onClick={() => setShowProjectFolderPicker((prev) => !prev)}
                  className="w-full px-3 py-2 text-left text-sm flex items-center justify-between transition-colors"
                  style={{ backgroundColor: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)', color: selectedProjectFolderLabel ? 'white' : 'rgba(255,255,255,0.35)' }}
                >
                  <span>{selectedProjectFolderLabel || 'Select a folder'}</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {showProjectFolderPicker && (
                  <div className="mt-1 max-h-64 overflow-y-auto" style={{ backgroundColor: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)' }}>
                    {hasAnySelectableFolder ? (
                      folderPickerTree.map((node) => (
                        <FolderPickerNode
                          key={node.id}
                          node={node}
                          depth={0}
                          selectedValue={selectedProjectFolderId}
                          onSelect={(value) => {
                            setSelectedProjectFolderId(value);
                            setShowProjectFolderPicker(false);
                            if (isSelectedFolderShared(value)) {
                              loadShareableUsers();
                              resetSharingState();
                            }
                          }}
                          allowRootSelection={false}
                        />
                      ))
                    ) : (
                      <p className="text-sm px-3 py-3" style={{ color: 'rgba(255,255,255,0.4)' }}>No folders available.</p>
                    )}
                  </div>
                )}
              </div>

              {/* Sharing section (only for shared folders) */}
              {isSelectedFolderShared(selectedProjectFolderId) && (
                <SharingPicker
                  users={shareableUsers}
                  selectedIds={shareUserIds}
                  onSelectedIdsChange={setShareUserIds}
                  shareWithAll={shareWithAll}
                  onShareWithAllChange={setShareWithAll}
                  currentUserId={user?.id}
                />
              )}

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={handleCancelNewProject}
                  className="px-4 py-2 text-xs font-medium uppercase tracking-wider transition-colors"
                  style={{ border: '1px solid var(--tqa-border-dark)', color: 'rgba(255,255,255,0.5)', backgroundColor: 'transparent' }}
                  disabled={creating}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-xs font-medium uppercase tracking-wider transition-colors hover:brightness-110 disabled:opacity-40"
                  style={{ backgroundColor: 'var(--tqa-green)', color: 'var(--tqa-black)' }}
                  disabled={creating}
                >
                  {creating ? 'Creating...' : 'Create Project'}
                </button>
              </div>
            </form>
          </div>
        </div>
        )}

        {/* New Folder Modal */}
        {showNewFolderModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0,0,0,0.75)' }}>
          <div className="p-6 w-96" style={{ backgroundColor: 'var(--tqa-surface)', border: '1px solid var(--tqa-border-dark)' }}>
            <h3 className="text-sm font-bold text-white uppercase tracking-widest mb-4">Create New Folder</h3>
            <form onSubmit={handleCreateFolder}>
              <div className="mb-4">
                <label htmlFor="folder-name" className="block text-xs font-medium uppercase tracking-wider mb-2" style={{ color: 'rgba(255,255,255,0.5)' }}>
                  Folder Name
                </label>
                <input
                  type="text"
                  id="folder-name"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  className="w-full px-3 py-2 text-sm text-white focus:outline-none"
                  style={{ backgroundColor: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)' }}
                  placeholder="Enter folder name"
                  autoFocus
                />
                {error && <p className="text-xs mt-1" style={{ color: 'var(--tqa-red)' }}>{error}</p>}
              </div>
              <div className="mb-4">
                <label htmlFor="folder-parent" className="block text-xs font-medium uppercase tracking-wider mb-2" style={{ color: 'rgba(255,255,255,0.5)' }}>
                  Parent Folder
                </label>
                <button
                  type="button"
                  id="folder-parent"
                  onClick={() => setShowParentFolderPicker((prev) => !prev)}
                  className="w-full px-3 py-2 text-left text-sm flex items-center justify-between"
                  style={{ backgroundColor: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)', color: selectedParentFolderLabel ? 'white' : 'rgba(255,255,255,0.35)' }}
                >
                  <span>{selectedParentFolderLabel || 'Select a parent folder'}</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {showParentFolderPicker && (
                  <div className="mt-1 max-h-64 overflow-y-auto" style={{ backgroundColor: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)' }}>
                    {folderPickerTree.map((node) => (
                      <FolderPickerNode
                        key={node.id}
                        node={node}
                        depth={0}
                        selectedValue={selectedParentFolderId}
                        onSelect={(value) => {
                          setSelectedParentFolderId(value);
                          setShowParentFolderPicker(false);
                          if (isSelectedFolderShared(value)) {
                            loadShareableUsers();
                            resetSharingState();
                          }
                        }}
                        allowRootSelection={true}
                      />
                    ))}
                  </div>
                )}
                <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.3)' }}>
                  Select where to create the new folder
                </p>
              </div>

              {/* Sharing section (only for shared folders) */}
              {isSelectedFolderShared(selectedParentFolderId) && (
                <SharingPicker
                  users={shareableUsers}
                  selectedIds={shareUserIds}
                  onSelectedIdsChange={setShareUserIds}
                  shareWithAll={shareWithAll}
                  onShareWithAllChange={setShareWithAll}
                  currentUserId={user?.id}
                />
              )}

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={handleCancelNewFolder}
                  className="px-4 py-2 text-xs font-medium uppercase tracking-wider transition-colors"
                  style={{ border: '1px solid var(--tqa-border-dark)', color: 'rgba(255,255,255,0.5)', backgroundColor: 'transparent' }}
                  disabled={creating}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-xs font-medium uppercase tracking-wider transition-colors hover:brightness-110 disabled:opacity-40"
                  style={{ backgroundColor: 'var(--tqa-surface-3)', color: 'white' }}
                  disabled={creating}
                >
                  {creating ? 'Creating...' : 'Create Folder'}
                </button>
              </div>
            </form>
          </div>
        </div>
        )}
      </aside>

      {isDesktop && typeof onResize === 'function' && (
        <div
          role="separator"
          tabIndex={0}
          aria-label="Resize sidebar"
          aria-orientation="vertical"
          aria-valuemin={minWidth}
          aria-valuemax={maxWidth}
          aria-valuenow={Math.round(width)}
          className="fixed top-0 h-screen z-20 hidden md:block"
          style={{ left: `${Math.max(0, width - 3)}px`, width: '6px', cursor: 'col-resize' }}
          onPointerDown={handleResizePointerDown}
          onKeyDown={handleResizeKeyDown}
        />
      )}

      <button
        type="button"
        onClick={onToggleCollapse}
        className="fixed -ml-3 top-1/2 -translate-y-1/2 w-6 h-12 flex items-center justify-center transition-colors z-20"
        style={{ left: `${width}px`, backgroundColor: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)', color: 'rgba(255,255,255,0.75)' }}
        aria-label="Collapse sidebar"
        title="Collapse sidebar"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>
    </>
  );
};

export default Sidebar;